import { Box, Skeleton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import {useEffect, useMemo, useState} from 'react';

// Components
import Table from 'src/components/Table';
import TextWithTooltip from 'src/components/common/TextWithTooltip';
import { StyledWhiteBox } from 'src/features/TariffsAndRules/config';

// Utilities
import { first, get, truncate } from 'lodash';
import ImageComponent from 'src/components/common/Image';
import { EmptyText } from 'src/utils/constants/config';
import { TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES } from 'src/utils/constants/imagesUrl';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

const TradeRemedies = ({ dataTradeRemedy, isFetching }: { dataTradeRemedy?: ITradeRemedy; isFetching: boolean }) => {
  const theme = useTheme();
  const [tradeRemedyItem, setTradeRemedyItem] = useState<ITradeRemedyData | null>(null);
  const dataExport = useMemo(() => {
    return get(tradeRemedyItem, 'trade_remedy_details[0].company_details', []);
  }, [tradeRemedyItem]);
  useEffect(() => {
    setTradeRemedyItem(null);
  }, [dataTradeRemedy])
  const configColumnTariff: ConfigCell<ITradeRemedyData>[] = [
    {
      id: 'subject',
      disablePadding: true,
      label: 'Subject',
      cell: (row: ITradeRemedyData) => {
        const subject = first(row.trade_remedy_details)?.measure_title ?? EmptyText;
        return (
          <Tooltip title={subject}>
            <Typography variant="body2" color="text.secondary">
              {truncate(subject, { length: 30 })}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: 'remedy_type',
      disablePadding: true,
      label: 'Remedy Type',
      cell: (row: ITradeRemedyData) => {
        return (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Typography variant="body2" color="secondary.main">
              {row.remedy_type || EmptyText}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'remedy_status',
      disablePadding: true,
      label: 'Remedy Status',
      cell: (row: ITradeRemedyData) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {get(row, 'trade_remedy_details[0].remedy_status', EmptyText)}
          </Typography>
        );
      },
    },
    {
      id: 'start_date',
      disablePadding: true,
      label: 'Start Date',
      cell: (row: ITradeRemedyData) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {get(row, 'trade_remedy_details[0].start_date', EmptyText)}
          </Typography>
        );
      },
    },
    {
      id: 'end_date',
      disablePadding: true,
      label: 'End Date',
      cell: (row: ITradeRemedyData) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {get(row, 'trade_remedy_details[0].end_date', EmptyText)}
          </Typography>
        );
      },
    },
  ];

  const configColumnExport: ConfigCell<ITradeRemedyCompanyDetails>[] = [
    {
      id: 'exporting_firm',
      disablePadding: true,
      label: 'Exporting Firm',
      cell: (row: ITradeRemedyCompanyDetails) => {
        return (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {row.exporting_firm}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'measure',
      disablePadding: true,
      label: 'Measure',
      cell: (row: ITradeRemedyCompanyDetails) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {row.duty}
          </Typography>
        );
      },
    },
    {
      id: 'note',
      disablePadding: true,
      label: 'Note',
      cell: (row: ITradeRemedyCompanyDetails) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {row.note}
          </Typography>
        );
      },
    },
  ];

  if (isFetching) {
    return <SkeletonContent />;
  }

  return (
    <StyledWhiteBox spacing={2}>
      <Typography variant="h6">
        Trade Remedies{' '}
        <TextWithTooltip title="Trade remedies are trade policy tools that allow governments to take remedial action against imports." />
      </Typography>
      {getArrayDefault(dataTradeRemedy?.trade_remedy_data).length > 0 ? (
        <Table
          minWidth={300}
          data={getArrayDefault(dataTradeRemedy?.trade_remedy_data)}
          configColumn={configColumnTariff}
          dataKey="remedy_id"
          isPagination={false}
          onRowClick={(data: ITradeRemedyData) => {
            setTradeRemedyItem(data);
          }}
          styledRow={(data: ITradeRemedyData) =>
            tradeRemedyItem?.remedy_id === data?.remedy_id
              ? {
                  background: alpha(theme.palette.secondary.light, 0.3),
                  ' &:hover': {
                    background: alpha(theme.palette.secondary.light, 0.3),
                  },
                }
              : {}
          }
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          p={2}
          bgcolor={theme.palette.background.paper2}
          borderRadius={theme.shape.borderRadius / 2}
        >
          <Box display="flex" flex={1}>
            <ImageComponent imgSrc={TARIFFS_AND_RULE_DEFAULT_PAGE_IMAGES.saving_money_bank} width={150} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Does not apply any trade remedy on the{' '}
            <Typography variant="subtitle2" component="span" color="text.secondary">
              Selected Product.
            </Typography>
          </Typography>
        </Stack>
      )}
      {tradeRemedyItem && (
        <Table data={dataExport} configColumn={configColumnExport} dataKey="status_code" isPagination={false} />
      )}
    </StyledWhiteBox>
  );
};

export default TradeRemedies;

const SkeletonContent = () => {
  return (
    <StyledWhiteBox spacing={2}>
      <Skeleton variant="rectangular" animation="wave" width={150} height={30} />
      <Stack>
        <Skeleton animation="wave" variant="rectangular" height={200} />
      </Stack>
    </StyledWhiteBox>
  );
};
